import type {Part, ActionEvent} from 'src/components/agentic-ui/types';
import React, {useCallback, useState} from 'react';
import {
  Button,
  CircularLoader,
  BodyLarge,
  BodyMedium,
  BodySmall,
  BasicDialog,
} from '@spaced-out/ui-design-system';
import {StatusIndicator} from '@spaced-out/ui-design-system/lib/components/StatusIndicator';
import {classify} from 'src/utils/classify';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {SUBCHAT_ABANDON_SENTINEL} from 'src/utils/ask-ai';
import type {QuestionnaireDraft} from 'src/types/ask-ai';
import {WidgetRenderer} from './widgets';
import {
  subChatStore,
  useSubChatStatus,
  useDockedWidget,
} from './sub-chat-store';
import css from './docked-region.css';


export interface DockedRegionProps {
  /** Send parts as a user message (reuses the panel's send pipeline). */
  onSend: (parts: Part[]) => Promise<void> | void;
  /** True while the server is streaming a response. */
  serverResponding: boolean;
  /** The normal composer to show when ABSENT or MIN. */
  composer: React.ReactNode;
  /** Publish a UI event to the grace event bus (used by docked widgets). */
  publishMessageAction?: (action: ActionEvent) => void;
}

export function DockedRegion({
  onSend,
  serverResponding,
  composer,
  publishMessageAction,
}: DockedRegionProps) {
  const {t} = useLabelTranslation();
  const status = useSubChatStatus();
  const dockedWidget = useDockedWidget();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSubmit = useCallback(
    async (qaText: string) => {
      subChatStore.getState().setDockedWidget(null);
      await onSend([{type: 'plain_text', data: qaText}]);
    },
    [onSend],
  );

  // Wizard progress is persisted in the store so it survives the
  // unmount/remount that minimize→maximize causes (stable refs, no re-render).
  const getQuestionnaireDraft = useCallback(
    () => subChatStore.getState().wizardDraft,
    [],
  );
  const handleQuestionnaireDraftChange = useCallback(
    (draft: QuestionnaireDraft) =>
      subChatStore.getState().setWizardDraft(draft),
    [],
  );

  const handleAbandonConfirm = useCallback(async () => {
    setConfirmOpen(false);
    // Abandoning closes the sub chat window, so the sentinel must be recorded
    // with the window already ABSENT (not 'max'). reset() releases the dock and
    // flips the store to 'absent' synchronously, so getRequestExtras stamps the
    // outgoing sentinel with sub_chat_window_status:'absent'. This is what lets
    // an old conversation know the docked flow was dismissed and must not be
    // re-popped on open.
    // Resetting before the send also means we always release the dock, even if
    // the sentinel send fails (the send pipeline surfaces its own error in the
    // message stream) — abandoning locally is the user's intent regardless.
    subChatStore.getState().reset();
    await onSend([{type: 'plain_text', data: SUBCHAT_ABANDON_SENTINEL}]);
  }, [onSend]);

  const abandonDialog = (
    <BasicDialog
      isOpen={confirmOpen}
      heading={t('Abandon_this_task', 'Abandon this task?')}
      body={
        <BodyLarge color="secondary">
          {t(
            'Are_you_sure_you_want_to_abandon_this_task_All_progress_will_be_lost',
            'Are you sure you want to abandon this task? All progress will be lost.',
          )}
        </BodyLarge>
      }
      confirmText={t('Abandon', 'Abandon')}
      abortText={t('Cancel', 'Cancel')}
      handleConfirm={handleAbandonConfirm}
      handleAbort={() => setConfirmOpen(false)}
      onClose={() => setConfirmOpen(false)}
      semantic="warning"
    />
  );

  if (status === 'absent') {
    return <>{composer}</>;
  }

  if (status === 'min') {
    return (
      <>
        <div className={classify(css.region, css.minRegion)}>
          <div className={css.minBar}>
            <div className={css.minBarStatus}>
              <StatusIndicator status="success" />
              {/* TODO: status verb ("Building") + task name will arrive as a
                  separate docked status widget; generic label for now. */}
              <BodySmall className={css.minBarLabel}>
                {t('Task_in_progress', 'Task in progress')}
              </BodySmall>
            </div>
            <div className={css.minBarControls}>
              <Button
                type="ghost"
                size="small"
                iconLeftName="arrows-up-down"
                onClick={() => subChatStore.getState().maximize()}
                ariaLabel={t('Restore', 'Restore')}
              />
              <Button
                type="ghost"
                size="small"
                iconLeftName="close"
                onClick={() => setConfirmOpen(true)}
                ariaLabel={t('Close', 'Close')}
              />
            </div>
          </div>
        </div>
        {composer}
        {abandonDialog}
      </>
    );
  }

  // status === 'max'
  if (dockedWidget) {
    return (
      <div className={css.region}>
        <WidgetRenderer
          // Remount on widget identity change so a follow-up questionnaire
          // starts fresh (index reset + default_value re-seeded).
          key={dockedWidget.id}
          part={dockedWidget}
          publishMessageAction={publishMessageAction}
          dockDisabled={serverResponding}
          onQuestionnaireSubmit={handleSubmit}
          getQuestionnaireDraft={getQuestionnaireDraft}
          onQuestionnaireDraftChange={handleQuestionnaireDraftChange}
          onDockMinimize={() => subChatStore.getState().minimize()}
          onDockClose={() => setConfirmOpen(true)}
        />
        {abandonDialog}
      </div>
    );
  }

  // MAX but no docked widget => post-submit processing.
  return (
    <>
      <div className={css.processing}>
        <CircularLoader ariaLabel={t('Processing', 'Processing')} />
        {/* TODO: processing hint text source — generic for now. */}
        <BodyMedium>
          {t('Processing_your_request', 'Processing your request…')}
        </BodyMedium>
        {/* Escape hatch: the composer is hidden in this state, so if the
            response hangs or never arrives the user can still abandon. */}
        <Button
          type="ghost"
          size="small"
          iconLeftName="close"
          onClick={() => setConfirmOpen(true)}
          ariaLabel={t('Close', 'Close')}
        />
      </div>
      {abandonDialog}
    </>
  );
}
